import {
  Badge,
  Box,
  Button,
  Group,
  Modal,
  Paper,
  SegmentedControl,
  Stack,
  Switch,
  Text,
} from "@mantine/core";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { IconCrop, IconPhoto, IconRefresh } from "@tabler/icons-react";
import { useEffect, useMemo, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { FormModalFooter } from "@/components/FormModalFooter";
import { ModalTitle } from "@/components/ModalTitle";
import { CHECKER_BACKGROUND, ImagePreview } from "@/components/ImagePreview";
import { useObjectUrl } from "@/hooks/useObjectUrl";
// ZORUNLU: kütüphane stil enjekte etmiyor, konumlandırma tamamen bu dosyadan geliyor. İmport
// olmadan bileşen hatasız ama tamamen bozuk render ediyor. main.jsx yerine burada duruyor —
// tek tüketici bu bileşen, ileride ayrı parçaya bölünürse stili de beraberinde götürsün.
import "react-easy-crop/react-easy-crop.css";
import { cropImage } from "@/lib/image";
import { notify } from "@/lib/notify";

// Menü yükleme ekranının birebir ölçüleri (menu/src/assets/css/loading.css). Önizleme buradan
// besleniyor — sayı değişirse iki dosya birlikte güncellenmeli.
const LOADING_STAGE = {
  background:
    "radial-gradient(130% 90% at 50% -10%, #ffffff 0%, #faf6f1 45%, #f1e9df 100%)",
  size: 80,
  radius: 12,
  rings: [96, 120, 144],
};

// Tekerlekle yakınlaştırma olay yağmuru üretiyor; önizleme jest durduktan sonra bir kez üretilsin.
const PREVIEW_DEBOUNCE_MS = 200;

// Kadraj alanı ve onun yerini alan dosya bırakma alanı AYNI yükseklikte olmak zorunda: dosya
// seçildiğinde pencerenin boyu değişirse tek ekran hissi bozulur.
const STAGE_HEIGHT = 340;

// Üç seçenek de bir şeyi feda ediyor; hangisi olduğunu seçicinin altında söylüyoruz, yoksa
// "Sığdır" ile "Kırpma" arasındaki fark ancak deneyerek anlaşılıyor.
const FIT_HINT = {
  crop: "Çerçeveyi doldurur, dışarıda kalan kısım atılır.",
  contain: "Görselin tamamı sığar, artan yeri bulanık kopyası doldurur.",
  none: "Görsel kendi oranıyla yüklenir.",
};

// Yükleme ekranını taklit eden canlı önizleme: kırpılan görsel gerçekte 80px'lik yuvarlatılmış bir
// kutuya oturuyor ve arkasında siyah halkalar var. Kadraj alanı bunu göstermiyor (orada görsel yüzlerce
// piksel); kullanıcı "logom neden kayboldu" sorusunu ancak yükledikten sonra soruyordu.
function LoadingStagePreview({ src }) {
  return (
    <Paper
      withBorder
      radius="md"
      h={150}
      style={{
        background: LOADING_STAGE.background,
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {LOADING_STAGE.rings.map((size, index) => (
        <Box
          key={size}
          w={size}
          h={size}
          style={{
            position: "absolute",
            border: "2px solid #000",
            borderRadius: "50%",
            // Canlıda halkalar dışa doğru sönerek büyüyor; durağan önizlemede aynı
            // sönümü sabit opaklıkla veriyoruz.
            opacity: 0.32 - index * 0.1,
          }}
        />
      ))}
      {src && (
        <img
          src={src}
          alt=""
          width={LOADING_STAGE.size}
          height={LOADING_STAGE.size}
          style={{
            objectFit: "contain",
            borderRadius: LOADING_STAGE.radius,
            position: "relative",
            zIndex: 10,
          }}
        />
      )}
    </Paper>
  );
}

// Tek bir kaynaktan hedefe göre görsel çıkarmak için kadraj arayüzü — pencere KABUĞU OLMADAN.
// Ayrı durmasının sebebi aynı arayüzün iki kabukta yaşaması: Ayarlar'da kendi penceresinde
// (aşağıdaki ImageCropModal), ürün/kategori/şube yüklemesinde yükleme penceresinin içinde.
//
// `file` NULL OLABİLİR: o zaman kadraj alanının yerini dosya bırakma alanı alıyor, düzenin geri
// kalanı (sağdaki çıktı sütunu, ipucu satırı, butonlar) aynen yerinde kalıyor. Amaç tek ekran:
// seçim ayrı bir adımken kullanıcı iki farklı işe bakıyormuş gibi hissediyordu. Ölçüler bilerek
// birebir aynı (STAGE_HEIGHT) — dosya seçilince hiçbir şey yer değiştirmesin.
//
// `onPickFile` verilmezse bırakma alanı hiç çizilmiyor: Ayarlar dosyayı sayfadaki kartlardan
// seçiyor ve buraya her zaman dolu geliyor.
//
// `aspect`: { width, height } — çıktının HEDEF ölçüsü; çerçeve oranı bundan hesaplanıyor ve
// kırpılan alan daha küçükse büyütülmüyor (bkz. lib/image).
//
// `plate`: { padding, label, note } — hedef küçük ve sabit bir kutuya basılıyorsa: kenar boşluğu ve
// o kutunun canlı taklidi. Yalnızca yükleme ekranı kullanıyor; varlığı sahnenin gösterileceği
// anlamına geliyor, ayrı bir bayrak yok.
//
// `type`: çıktı biçimi. PNG (varsayılan) marka görselleri için — zemin şeffaf kalabilir. Fotoğraf
// yüzeyleri (ürün, kategori, şube) 'image/jpeg' veriyor: aynı kadrajın PNG'si megabaytlarca, ve
// JPEG alfa taşımadığı için ŞEFFAFLIK ANAHTARI DA GİZLENİYOR — kapatılamayan bir seçeneği açık
// göstermek yalan olurdu. Ayrı bir bayrak yok, biçim ikisini birden belirliyor.
//
// PNG çıktıda SVG bir kaynak vektör keskinliğini kaybediyor. Hedef ölçüler 400–800px; bu
// boyutlarda raster ile vektör arasında görünür fark yok.
export function ImageCropper({
  file,
  aspect,
  plate,
  onConfirm,
  confirming,
  onCancel,
  // Ayarlar'da bu buton işlemi tamamen iptal ediyor ("Vazgeç"), yükleme penceresinde adıma göre
  // "Kapat" ya da "Değiştir" oluyor. Aynı davranış, farklı vaat.
  cancelLabel = "Vazgeç",
  // Butonların soluna eklenen ek eylem (yükleme penceresinde "Görseli Kaldır").
  footerStart,
  onPickFile,
  maxSourceSize,
  dropHint,
  type = "image/png",
  quality,
}) {
  const { url, failed: sourceFailed } = useObjectUrl(file);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [stageUrl, setStageUrl] = useState(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const stageUrlRef = useRef(null);
  // Kırpma+kodlama, üst bileşenin isPending'i kalkmadan ÖNCE sürüyor (800×800'de birkaç yüz ms).
  // O aralıkta buton hâlâ tıklanabilirdi: çift tık iki tam yükleme başlatıyor ve putImage her
  // ikisinde de aynı eski URL'i silmeye çalıştığı için kaybeden anahtar R2'de yetim kalıyordu.
  const [cropping, setCropping] = useState(false);
  const transparent = type === "image/png";
  // PNG'de varsayılan AÇIK: logolar çoğunlukla alfa kanalı olmadan dışa aktarılıyor (zemin rengi
  // dosyanın içinde) ve üç marka hedefinin üçü de şeffaf istiyor. JPEG'de anahtar hiç yok.
  const [removeBackground, setRemoveBackground] = useState(transparent);
  // Çıktının çerçeveyle ilişkisi (bkz. lib/image cropImage `fit`).
  //
  // Fotoğraf hedeflerinde varsayılan 'none': kullanıcının yüklediği görsel olduğu gibi kaydediliyor,
  // kırpma kararı ancak isteyerek veriliyor.
  //
  // Marka hedeflerinde (PNG) seçici GİZLİ ama durum yine de çıktıyı belirliyor — orada 'crop'
  // zorunlu: logo/amblem/yükleme ekranı sabit kutulara basılıyor ve kadrajı kullanıcı seçiyor.
  // Varsayılanı ayırmasaydım gizli bir kontrol marka görsellerini kadrajsız yükletirdi.
  const [fit, setFit] = useState(transparent ? "crop" : "none");

  const outputOptions = useMemo(
    () => ({
      width: aspect.width,
      height: aspect.height,
      padding: plate?.padding,
      removeBackground,
      type,
      quality,
      fit,
    }),
    [
      aspect.width,
      aspect.height,
      plate?.padding,
      removeBackground,
      type,
      quality,
      fit,
    ],
  );

  // Önizleme gerçek çıktıdan üretiliyor, taklit bir CSS düzeninden değil: kullanıcının gördüğü
  // kare ile yüklenen dosya birebir aynı.
  //
  // Eski URL, YENİSİ hazır olunca bırakılıyor (temizlik fonksiyonunda değil): temizlik kadraj
  // değişir değişmez çalışıyor ve URL'i yeni kare üretilmeden iptal ediyordu — <img> bir sonraki
  // kırpma bitene kadar kırık kalıyordu.
  useEffect(() => {
    if (!area || !file) {
      return undefined;
    }

    let cancelled = false;

    // Sürükleme bitişte TEK olay üretiyor ama yakınlaştırma her adımda üretiyor — tekerlek de
    // kıstırma da aynı yoldan geçiyor ve react-easy-crop yalnızca yeniden boyutlandırmayı
    // geciktiriyor. Gecikme olmadan tek bir jest onlarca tam kırpma+PNG kodlaması tetikliyor ve
    // hepsi aynı anda bellekte duruyordu.
    const timer = setTimeout(() => {
      cropImage(file, area, outputOptions)
        .then((cropped) => {
          const next = URL.createObjectURL(cropped);

          if (cancelled) {
            URL.revokeObjectURL(next);

            return;
          }

          if (stageUrlRef.current) {
            URL.revokeObjectURL(stageUrlRef.current);
          }

          stageUrlRef.current = next;
          setStageUrl(next);
          setPreviewFailed(false);
        })
        .catch((error) => {
          console.warn("[panel] preview failed", error);

          // Eski kare artık kullanıcının ayarlarıyla eşleşmiyor. Ekranda bırakmak,
          // onaylarken yüklenecek olandan başka bir görüntüye bakmak demek.
          if (!cancelled) {
            setStageUrl(null);
            setPreviewFailed(true);
          }
        });
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [file, area, outputOptions]);

  // Son önizleme URL'i sahipsiz kalırdı. Pencere `opened` bayrağıyla değil mount/unmount ile
  // açılıp kapandığı için temizlik koşulsuz olmak zorunda — `opened` sabit true'ydu ve koşullu
  // sürüm hiçbir zaman bir temizleyici kaydetmiyordu.
  useEffect(
    () => () => {
      if (stageUrlRef.current) {
        URL.revokeObjectURL(stageUrlRef.current);
        stageUrlRef.current = null;
      }
    },
    [],
  );

  // Cropper bir sınıf bileşeni; referans kimliğini sabit tutmanın kazancı yok.
  const handleCropComplete = (_, areaPixels) => setArea(areaPixels);

  // Uzaklaşıp kaydırdıktan sonra başlangıç kadrajına dönmenin yolu yoktu: kaydırıcı da
  // kaldırıldığı için tekerleği tam 1.00'e getirmek elle imkânsızdı, pencereyi kapatıp açmak
  // tek çareydi.
  const framed = zoom !== 1 || crop.x !== 0 || crop.y !== 0;
  const resetFraming = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleConfirm = async () => {
    if (!area || cropping) {
      return;
    }

    setCropping(true);

    let cropped;

    try {
      cropped = await cropImage(file, area, outputOptions);
    } catch (error) {
      console.warn("[panel] crop failed", error);
      notify.error("Görsel kırpılamadı");
      setCropping(false);

      return;
    }

    try {
      // onConfirm AYRI bir try'da: yükleme hatasını "kırpılamadı" diye bildirmek yanlış yeri
      // işaret ediyordu.
      await onConfirm(cropped);
    } finally {
      setCropping(false);
    }
  };

  return (
    <>
      <Group align="flex-start" gap="lg" wrap="nowrap">
        <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
          {/* Kadraj zemini çıktının biçimine göre, çünkü ikisi farklı soruyu yanıtlıyor.
                        PNG'de soru "arkası gerçekten şeffaf mı": şeffaf logolar açık zeminde
                        kayboluyor ve kenarın nerede bittiği damalı desende okunmuyor, o yüzden düz
                        koyu. JPEG'de şeffaflık zaten yok, soru "kadrajım doğru mu": koyu zemin
                        fotoğrafı olduğundan karanlık gösterip pencereyi ağırlaştırıyordu, Ayarlar
                        sayfasındaki damalı kutunun aynısı kullanılıyor. */}
          {file || !onPickFile ? (
            <Paper
              withBorder
              radius="md"
              h={STAGE_HEIGHT}
              style={{
                position: "relative",
                overflow: "hidden",
                ...(transparent
                  ? { background: "var(--mantine-color-dark-6)" }
                  : CHECKER_BACKGROUND),
              }}
            >
              {sourceFailed && (
                <Text size="sm" c="dimmed" ta="center" p="xl">
                  Bu dosya görüntülenemedi. Başka bir görsel seç.
                </Text>
              )}
              {url && (
                <Cropper
                  image={url}
                  crop={crop}
                  zoom={zoom}
                  minZoom={0.4}
                  maxZoom={4}
                  restrictPosition={false}
                  aspect={aspect.width / aspect.height}
                  objectFit="contain"
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={handleCropComplete}
                />
              )}
            </Paper>
          ) : (
            <Dropzone
              onDrop={onPickFile}
              accept={IMAGE_MIME_TYPE}
              maxSize={maxSourceSize}
              h={STAGE_HEIGHT}
              radius="md"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Stack align="center" gap={8} c="dimmed">
                <IconPhoto size={38} stroke={1.2} />
                <Text size="sm" ta="center" maw={300}>
                  {dropHint}
                </Text>
              </Stack>
            </Dropzone>
          )}

          {/* Yakınlaştırma kaydırıcısı kaldırıldı; tekerlek/kıstırma zaten çalışıyor ama
                        görünür bir ipucu olmadan keşfedilmiyor. Satır dosya yokken de duruyor:
                        sonradan belirse kadrajın altındaki her şeyi aşağı iterdi. */}
          <Group justify="center" gap="xs" wrap="nowrap">
            <Text size="xs" c="dimmed">
              {!file
                ? "Görsel seçince kadrajlanır"
                : fit === "crop"
                  ? "Sürükleyerek konumlandır, tekerlekle yakınlaştır"
                  : "Görselin tamamı kullanılıyor — kadraj devre dışı"}
            </Text>
            {/* Buton hep duruyor, dokunulmadığında sadece pasif: görünüp kaybolan bir
                            kontrol kadrajın altındaki satırın genişliğini oynatıyor ve varlığı ancak
                            iş işten geçtikten sonra fark ediliyordu. */}
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              leftSection={<IconRefresh size={12} />}
              disabled={!framed}
              onClick={resetFraming}
            >
              Sıfırla
            </Button>
          </Group>
        </Stack>

        {/* Sağ sütun tek bir soruyu yanıtlıyor: "ne yüklenecek". Önizleme gerçek çıktı
                    dosyasından geliyor — zemin temizliği ve kenar boşluğu dahil. Anahtar da burada,
                    çünkü etkisi doğrudan üstündeki karede görünüyor. */}
        <Stack w={210} gap="xs">
          <Text size="xs" fw={700} tt="uppercase" c="dimmed">
            {plate?.label ?? "Çıktı önizlemesi"}
          </Text>

          {plate ? (
            <LoadingStagePreview src={stageUrl} />
          ) : (
            <ImagePreview
              src={stageUrl}
              alt="Çıktı"
              height={150}
              empty={
                !file
                  ? "Görsel seç"
                  : previewFailed
                    ? "Önizleme üretilemedi"
                    : "Kadrajı ayarla"
              }
            />
          )}

          {/* Yalnızca ölçü. Biçim rozeti (PNG/JPG) kaldırıldı: kullanıcının verdiği bir
                        karar değil, hedefin kendisi belirliyor — bilgi taşımadan yer kaplıyordu.
                        "Kırpma"da çıktı hedef ölçüde DEĞİL, kaynağın kendi oranında; sabit sayıyı
                        göstermek yanlış bilgi olurdu. */}
          <Badge variant="light" color="gray" size="sm" radius="sm">
            {fit === "none" ? "Özgün oran" : `${aspect.width}×${aspect.height}`}
          </Badge>

          {plate?.note && (
            <Text size="xs" c="dimmed">
              {plate.note}
            </Text>
          )}

          {/* Zemin rengi kadrajın köşelerinden okunuyor (beyaz, krem, koyu — fark etmez).
                        Zemin düz değilse ya da kadraj içeriğin içine girdiyse temizlik kendini iptal
                        ediyor. Elle kapatmak gereken tek durum: logonun İÇİNDE zeminle aynı renkte
                        bir alan olması (koyu rozet üstünde beyaz yazı gibi) — o da şeffaflaşır. */}
          {transparent ? (
            <Switch
              mt="xs"
              size="sm"
              color="chocolate"
              checked={removeBackground}
              onChange={(event) =>
                setRemoveBackground(event.currentTarget.checked)
              }
              label="Zemini şeffaf yap"
            />
          ) : (
            <Stack gap={4} mt="xs">
              <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                Çerçeveye oturma
              </Text>
              <SegmentedControl
                fullWidth
                size="xs"
                color="chocolate"
                value={fit}
                onChange={setFit}
                data={[
                  { value: "none", label: "Kırpma" },
                  { value: "contain", label: "Sığdır" },
                  { value: "crop", label: "Kırp" },
                ]}
              />
              <Text size="xs" c="dimmed">
                {FIT_HINT[fit]}
              </Text>
            </Stack>
          )}
        </Stack>
      </Group>

      <FormModalFooter>
        {footerStart}
        <Button variant="default" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button
          leftSection={<IconCrop size={16} />}
          disabled={!area}
          loading={confirming || cropping}
          onClick={handleConfirm}
        >
          Yükle
        </Button>
      </FormModalFooter>
    </>
  );
}

// Kadraj arayüzünün TEK BAŞINA pencere hâli. Ayarlar sayfası kullanıyor: orada dosya seçimi
// sayfanın kendi kartlarında (FileButton) yapılıyor, yani açılacak bir yükleme penceresi yok ve
// kadraj doğrudan pencere oluyor.
export function ImageCropModal({
  onClose,
  title,
  description,
  ...cropperProps
}) {
  return (
    <Modal
      opened
      onClose={onClose}
      centered
      radius="lg"
      size="xl"
      padding="lg"
      title={
        <ModalTitle icon={IconCrop} title={title} description={description} />
      }
    >
      <ImageCropper onCancel={onClose} {...cropperProps} />
    </Modal>
  );
}
